/* eslint-disable */
//  frontendGE/material-dashboard-react-main/src/layouts/visitMachine/collectionWorkflow.js

import { toFormMachine, toPayloadFromForm } from "utils/visitMappers";
import { hasRent } from "utils/rent";
import {
  getMachineTypeName,
  sortLocalMachinesFixed,
  sortTypeEntries,
} from "utils/localMachineOrder";
import { getMachineFormula, hasMachineOperatorFormula } from "utils/machineFormula";
import { hasPresentationRent, resolveMachineSerial } from "utils/collectionMachinePresentation";
import {
  isInsertedInCurrentVisitNoticeRow,
  isVisitMachineCollected,
} from "utils/visitCollectionState";

export const getTypeName = (machine) =>
  machine?.MachineType?.Type || machine?.MachineType || machine?.MachineTypeType || null;

const isPresentCollectionInputValue = (value) => value !== undefined && value !== null && value !== "";

const pickCollectionInputNumber = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (!isPresentCollectionInputValue(value)) continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return undefined;
};

const pickFirstPresentCollectionInputValue = (...values) =>
  values.find(isPresentCollectionInputValue);

const buildResetInputsFromMachine = (machine = {}) => ({
  HasReset: Boolean(machine.HasReset),
  PostResetStartIn: pickCollectionInputNumber(
    machine,
    "PostResetStartIn",
    "postResetStartIn"
  ) ?? 0,
  PostResetStartOut: pickCollectionInputNumber(
    machine,
    "PostResetStartOut",
    "postResetStartOut"
  ) ?? 0,
});

export const buildCollectionUserInputsFromMachines = (machines = []) =>
  (machines || []).reduce((acc, machine) => {
    if (isInsertedInCurrentVisitNoticeRow(machine)) return acc;

    const localMachineId = Number(machine?.LocalMachineId ?? machine?.Id);
    if (!Number.isFinite(localMachineId) || localMachineId <= 0) return acc;

    const type = getTypeName(machine);
    const fType = String(
      machine?.Formula?.Type ?? machine?.FormulaType ?? machine?.MachineFormulaType ?? ""
    ).trim();
    const hasFormula = ["+", "-", "*", "/"].includes(fType);
    const commonInputs = {
      Note: pickFirstPresentCollectionInputValue(machine?.note, machine?.Note, ""),
      WasCollected: isVisitMachineCollected(machine),
    };

    if (type === "Rockola") {
      acc[localMachineId] = {
        MoneyIn: pickCollectionInputNumber(machine, "moneyIn", "MoneyIn"),
        ...commonInputs,
      };
    } else if (type === "Peluchera") {
      acc[localMachineId] = {
        CreditsIn: pickCollectionInputNumber(machine, "actualCreditsIn", "CreditsIn"),
        CreditsOut: pickCollectionInputNumber(machine, "actualCreditsOut", "CreditsOut"),
        netCreditsOut: pickCollectionInputNumber(machine, "netCreditsOut", "NetCreditsOut"),
        ...commonInputs,
      };
    } else if (type === "Tragamoneda") {
      const resetInputs = buildResetInputsFromMachine(machine);
      if (hasFormula) {
        if (Boolean(machine?.IsAccumulative)) {
          acc[localMachineId] = {
            CreditsIn: pickCollectionInputNumber(machine, "actualCreditsIn", "CreditsIn"),
            CreditsOut: pickCollectionInputNumber(machine, "actualCreditsOut", "CreditsOut"),
            ...commonInputs,
            ...resetInputs,
          };
        } else {
          acc[localMachineId] = {
            NetCreditsIn: pickCollectionInputNumber(machine, "netCreditsIn", "NetCreditsIn"),
            NetCreditsOut: pickCollectionInputNumber(machine, "netCreditsOut", "NetCreditsOut"),
            ...commonInputs,
            ...resetInputs,
          };
        }
      } else if (Boolean(machine?.IsAccumulative)) {
        acc[localMachineId] = {
          MoneyIn: pickCollectionInputNumber(machine, "moneyIn", "MoneyIn"),
          MoneyOut: pickCollectionInputNumber(machine, "moneyOut", "MoneyOut"),
          ...commonInputs,
          ...resetInputs,
        };
      } else {
        acc[localMachineId] = {
          NetMoneyIn: pickCollectionInputNumber(machine, "netMoneyIn", "NetMoneyIn"),
          NetMoneyOut: pickCollectionInputNumber(machine, "netMoneyOut", "NetMoneyOut"),
          ...commonInputs,
          ...resetInputs,
        };
      }
    } else {
      acc[localMachineId] = {
        CreditsIn: pickCollectionInputNumber(machine, "actualCreditsIn", "CreditsIn") ?? null,
        CreditsOut: pickCollectionInputNumber(machine, "actualCreditsOut", "CreditsOut") ?? null,
        HasReset: Boolean(machine.HasReset),
        ...commonInputs,
      };
    }

    acc[localMachineId] = {
      ...acc[localMachineId],
      ...buildCounterInputsFromVerification(machine?.CounterVerification),
    };

    return acc;
  }, {});

export const getDraftPersistedMachineCount = (progress = null) => {
  if (Array.isArray(progress?.visitMachines)) return progress.visitMachines.length;
  const completed = Number(progress?.completedCount ?? progress?.CompletedCount);
  const notCollected = Number(progress?.notCollectedCount ?? progress?.NotCollectedCount);
  if (Number.isFinite(completed) || Number.isFinite(notCollected)) {
    return (Number.isFinite(completed) ? completed : 0) +
      (Number.isFinite(notCollected) ? notCollected : 0);
  }
  return 0;
};

export const getDraftDateEditState = ({
  isEditMode = false,
  persistedMachineCount = 0,
  isDateChangeRequested = false,
  persistedDate = "",
  candidateDate = "",
  validationMessage = null,
  isLoadingBounds = false,
  hasActiveOperation = false,
  hasOpenMachineEditor = false,
} = {}) => {
  if (isEditMode) {
    return {
      isDraftDateFlow: false,
      canEditDate: false,
      isLockedByPersistedMachines: false,
      changed: false,
      canApply: false,
      requiresResetConfirmation: false,
      showModifyButton: false,
      showCancelChangeButton: false,
    };
  }

  const count = Number(persistedMachineCount);
  const hasPersistedMachines = Number.isFinite(count) && count > 0;
  const canEditDate =
    !hasOpenMachineEditor &&
    !hasActiveOperation &&
    (!hasPersistedMachines || isDateChangeRequested === true);
  const changed = Boolean(candidateDate) && Boolean(persistedDate) && candidateDate !== persistedDate;
  const canApply =
    canEditDate &&
    changed &&
    !validationMessage &&
    !isLoadingBounds;

  return {
    isDraftDateFlow: true,
    canEditDate,
    isLockedByPersistedMachines: hasPersistedMachines && !isDateChangeRequested,
    changed,
    canApply,
    requiresResetConfirmation: hasPersistedMachines && canApply,
    showModifyButton: hasPersistedMachines && !isDateChangeRequested,
    showCancelChangeButton: hasPersistedMachines && isDateChangeRequested,
  };
};

export const buildCollectionPreviousValuesRequestKey = ({
  localId,
  visitDateTime,
  isEditMode = false,
  excludeVisitId = null,
} = {}) => {
  const parsedLocalId = Number(localId);
  if (!Number.isFinite(parsedLocalId) || parsedLocalId <= 0) return null;
  const normalizedExcludeVisitId = isEditMode && Number.isFinite(Number(excludeVisitId))
    ? Number(excludeVisitId)
    : "";
  return [
    parsedLocalId,
    visitDateTime == null ? "" : String(visitDateTime),
    isEditMode ? "edit" : "creation",
    normalizedExcludeVisitId,
  ].join("|");
};

export const getNextCollectionPreviousValuesRequestState = ({
  currentLoading = null,
  requestKey = null,
  currentRequestId = 0,
  force = false,
} = {}) => {
  if (!requestKey) {
    return { shouldStart: false, requestId: currentRequestId, loading: currentLoading };
  }
  if (!force && currentLoading?.key === requestKey) {
    return { shouldStart: false, requestId: currentRequestId, loading: currentLoading };
  }
  const requestId = Number(currentRequestId) + 1;
  return {
    shouldStart: true,
    requestId,
    loading: { key: requestKey, requestId },
  };
};

export const groupCollectionMachinesLikeLocalMachineIndex = (machines = []) => {
  const notRentedByType = {};
  const rentedByType = {};
  let totalRented = 0;

  sortLocalMachinesFixed(machines, { preferLocalDisplayNumber: true }).forEach((machine) => {
    const type = getMachineTypeName(machine);
    const isRentedForPresentation = hasPresentationRent(machine);
    const target = isRentedForPresentation ? rentedByType : notRentedByType;
    if (!target[type]) target[type] = [];
    target[type].push(machine);
    if (isRentedForPresentation) totalRented += 1;
  });

  return {
    notRentedByType,
    rentedByType,
    notRentedEntries: sortTypeEntries(notRentedByType),
    rentedEntries: sortTypeEntries(rentedByType),
    totalRented,
  };
};

const hasUsableCollectionLocalValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const mergeDefinedCollectionValues = (base = {}, override = {}) => {
  const merged = { ...(base || {}) };
  Object.entries(override || {}).forEach(([key, value]) => {
    if (hasUsableCollectionLocalValue(value)) merged[key] = value;
  });
  return merged;
};

const toPositiveCollectionId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const normalizeVisitDetailLocal = ({ local = null, person = undefined } = {}) => {
  if (!local) return null;
  const normalizedPerson =
    local?.Person != null
      ? local.Person
      : person !== undefined
        ? person
        : null;

  return {
    ...local,
    Person: normalizedPerson ?? null,
  };
};

export const mergeCollectionLocalSources = ({
  stateLocal = null,
  bootLocal = null,
  expectedLocalId = null,
} = {}) => {
  const normalizedState = normalizeVisitDetailLocal({ local: stateLocal });
  const normalizedBoot = normalizeVisitDetailLocal({ local: bootLocal });
  const expectedId = toPositiveCollectionId(expectedLocalId);
  const stateId = toPositiveCollectionId(normalizedState?.Id);
  const bootId = toPositiveCollectionId(normalizedBoot?.Id);

  const stateMatches = normalizedState && (!expectedId || !stateId || stateId === expectedId);
  const bootMatches = normalizedBoot && (!expectedId || !bootId || bootId === expectedId);

  if (expectedId && stateId && bootId && stateId !== bootId) {
    if (bootId === expectedId) return normalizedBoot;
    if (stateId === expectedId) return normalizedState;
    return null;
  }

  if (normalizedState && !stateMatches && bootMatches) return normalizedBoot;
  if (normalizedBoot && !bootMatches && stateMatches) return normalizedState;
  if (!normalizedState || !stateMatches) return bootMatches ? normalizedBoot : null;
  if (!normalizedBoot || !bootMatches) return normalizedState;

  const mergedPerson = mergeDefinedCollectionValues(
    normalizedBoot.Person || {},
    normalizedState.Person || {}
  );

  return {
    ...mergeDefinedCollectionValues(normalizedBoot, normalizedState),
    Person:
      normalizedBoot.Person || normalizedState.Person
        ? mergedPerson
        : null,
  };
};

export const resolveCollectionHeaderData = ({ local = null } = {}) => ({
  localName: hasUsableCollectionLocalValue(local?.Name) ? local.Name : "Local",
  clientName: hasUsableCollectionLocalValue(local?.Person?.Name)
    ? local.Person.Name
    : "Cliente",
});

export const isBlankUserInputValue = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

export const toNonNegativeCounterNumber = (value) => {
  if (isBlankUserInputValue(value)) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

export const buildCounterInputsFromVerification = (counterVerification) => {
  if (!counterVerification) return {};

  const status = String(counterVerification.Status || "");
  const reason =
    counterVerification.UnverifiedReason == null
      ? null
      : String(counterVerification.UnverifiedReason);
  const note = counterVerification.Note ?? "";

  if (["verified_exact", "verified_with_tolerance", "verified_incorrect"].includes(status)) {
    return {
      CounterMode: "verify",
      CounterCurrentIn: counterVerification.CurrentCounterIn ?? "",
      CounterCurrentOut: counterVerification.CurrentCounterOut ?? "",
      CounterNote: note,
    };
  }

  if (status === "unverified" && reason === "not_read") {
    return {
      CounterMode: "not_read",
      CounterNote: note,
    };
  }

  if (status === "unverified" && reason === "counter_reset") {
    return {
      CounterMode: "counter_reset",
      CounterCurrentIn: counterVerification.CurrentCounterIn ?? "",
      CounterCurrentOut: counterVerification.CurrentCounterOut ?? "",
      CounterPostResetIn: counterVerification.PostResetCounterIn ?? "",
      CounterPostResetOut: counterVerification.PostResetCounterOut ?? "",
      CounterNote: note,
    };
  }

  if (status === "unverified" && reason === "other") {
    return {
      CounterMode: "verify",
      CounterCurrentIn: counterVerification.CurrentCounterIn ?? "",
      CounterCurrentOut: counterVerification.CurrentCounterOut ?? "",
      CounterNote: note,
    };
  }

  return {};
};

export const getCounterMachineLabel = (machine) => {
  const name =
    machine?.Machine?.Name ??
    machine?.MachineName ??
    machine?.Name ??
    `LM ${machine?.LocalMachineId ?? machine?.Id ?? ""}`;
  return `Máquina ${name}`;
};

export const shouldSendCounterVerificationForMachine = (
  machine,
  counterVerificationContextsByLocalMachineId = {}
) => {
  if (isInsertedInCurrentVisitNoticeRow(machine)) return false;
  const lmId = Number(machine?.LocalMachineId ?? machine?.Id ?? 0);
  const context = counterVerificationContextsByLocalMachineId?.[lmId];
  return (
    getTypeName(machine) === "Tragamoneda" &&
    !hasRent(machine) &&
    isVisitMachineCollected(machine) === true &&
    context?.hasCounterConfig === true
  );
};

export const buildCounterVerificationPayloadForMachine = ({
  machine,
  userInputs = {},
  counterVerificationContextsByLocalMachineId = {},
}) => {
  if (!shouldSendCounterVerificationForMachine(machine, counterVerificationContextsByLocalMachineId)) {
    return { payload: null, error: null };
  }

  const lmId = Number(machine?.LocalMachineId ?? machine?.Id ?? 0);
  const inputs = userInputs?.[lmId] || {};
  const mode = inputs.CounterMode || "verify";
  const note = isBlankUserInputValue(inputs.CounterNote) ? null : String(inputs.CounterNote).trim();
  const machineLabel = getCounterMachineLabel(machine);

  if (mode === "not_read") {
    return {
      payload: {
        Mode: "not_read",
        Note: note,
      },
      error: null,
    };
  }

  if (mode === "counter_reset") {
    const missingPostReset =
      isBlankUserInputValue(inputs.CounterPostResetIn) ||
      isBlankUserInputValue(inputs.CounterPostResetOut);
    if (missingPostReset) {
      return { payload: null, error: `${machineLabel}: completá los valores post-reset de contadores.` };
    }
    const currentIn = isBlankUserInputValue(inputs.CounterCurrentIn)
      ? null
      : toNonNegativeCounterNumber(inputs.CounterCurrentIn);
    const currentOut = isBlankUserInputValue(inputs.CounterCurrentOut)
      ? null
      : toNonNegativeCounterNumber(inputs.CounterCurrentOut);
    const postResetIn = toNonNegativeCounterNumber(inputs.CounterPostResetIn);
    const postResetOut = toNonNegativeCounterNumber(inputs.CounterPostResetOut);
    if (
      (!isBlankUserInputValue(inputs.CounterCurrentIn) && currentIn == null) ||
      (!isBlankUserInputValue(inputs.CounterCurrentOut) && currentOut == null)
    ) {
      return { payload: null, error: `${machineLabel}: los valores de lectura actual pre-reset deben ser enteros no negativos.` };
    }
    if (postResetIn == null || postResetOut == null) {
      return { payload: null, error: `${machineLabel}: los valores post-reset deben ser enteros no negativos.` };
    }
    return {
      payload: {
        Mode: "counter_reset",
        CurrentCounterIn: currentIn,
        CurrentCounterOut: currentOut,
        PostResetCounterIn: postResetIn,
        PostResetCounterOut: postResetOut,
        Note: note,
      },
      error: null,
    };
  }

  if (mode !== "verify") {
    return { payload: null, error: `${machineLabel}: modo de contadores inválido.` };
  }

  const currentIn = toNonNegativeCounterNumber(inputs.CounterCurrentIn);
  const currentOut = toNonNegativeCounterNumber(inputs.CounterCurrentOut);
  const missingCurrent =
    isBlankUserInputValue(inputs.CounterCurrentIn) ||
    isBlankUserInputValue(inputs.CounterCurrentOut);
  if (missingCurrent) {
    return { payload: null, error: `${machineLabel}: completá entrada y salida de contadores.` };
  }
  if (currentIn == null || currentOut == null) {
    return { payload: null, error: `${machineLabel}: los valores de contadores deben ser enteros no negativos.` };
  }

  return {
    payload: {
      Mode: "verify",
      CurrentCounterIn: currentIn,
      CurrentCounterOut: currentOut,
      Note: note,
    },
    error: null,
  };
};

export const validateCollectionBeforeSave = ({
  machines = [],
  userInputs = {},
  counterVerificationContextsByLocalMachineId = {},
  hasOperatorFormula,
}) => {
  const errors = [];

  for (const m of machines) {
    if (isInsertedInCurrentVisitNoticeRow(m)) continue;

    const type = getTypeName(m);
    const lmId = Number(m?.LocalMachineId ?? m?.Id ?? 0);
    const wasCollected = isVisitMachineCollected(m);
    if (!wasCollected) continue;

    const isRented = hasRent(m);
    const isCash = type === "Rockola";

    const formula = getMachineFormula(m);
    const hasFormula =
      typeof hasOperatorFormula === "function"
        ? Boolean(hasOperatorFormula(m))
        : hasMachineOperatorFormula(m);
    const formulaId = hasFormula ? formula?.Id ?? null : null;
    const formulaValueRaw = hasFormula ? formula?.Value ?? null : null;
    const formulaValueNum = Number(formulaValueRaw);

    if (isRented || isCash) {
      if (formulaId != null || hasFormula) {
        errors.push(
          `[${type ?? "Sin tipo"}] localMachineId ${lmId}: NO debe tener fórmula (por ser ${isRented ? "alquilada" : "Rockola"}).`
        );
      }
      continue;
    }

    if (["Pool", "Tejo", "Peluchera"].includes(type)) {
      if (!hasFormula) {
        errors.push(
          `[${type}] localMachineId ${lmId}: falta fórmula válida (+ - * /).`
        );
      }
      if (!Number.isFinite(formulaValueNum)) {
        errors.push(
          `[${type}] localMachineId ${lmId}: falta FormulaValue numérico.`
        );
      }
      continue;
    }

    if (type === "Tragamoneda") {
      const counterResult = buildCounterVerificationPayloadForMachine({
        machine: m,
        userInputs,
        counterVerificationContextsByLocalMachineId,
      });
      if (counterResult.error) {
        errors.push(counterResult.error);
      }
      const rawInput = userInputs?.[lmId] || {};
      const hasReset =
        rawInput?.HasReset === true ||
        (rawInput?.HasReset == null && m?.HasReset === true);

      if (hasReset) {
        const postResetStartIn = rawInput?.PostResetStartIn;
        const postResetStartOut = rawInput?.PostResetStartOut;
        const missingPostResetValues =
          isBlankUserInputValue(postResetStartIn) || isBlankUserInputValue(postResetStartOut);

        if (missingPostResetValues) {
          errors.push(
            `[Tragamoneda] Máquina LM ${lmId}: si reseteás, debés completar los valores post-reset (pueden ser 0).`
          );
        }
      }

      const isAccumulativeMachine = Boolean(m?.IsAccumulative);
      if (!hasFormula) {
        if (isAccumulativeMachine && (m?.moneyIn == null || m?.moneyOut == null)) {
          errors.push(
            `[Tragamoneda] localMachineId ${lmId}: sin fórmula requiere MoneyIn y MoneyOut.`
          );
        }
        if (!isAccumulativeMachine && (m?.netMoneyIn == null || m?.netMoneyOut == null)) {
          errors.push(
            `[Tragamoneda] localMachineId ${lmId}: sin fórmula con borrado requiere NetMoneyIn y NetMoneyOut.`
          );
        }
      } else {
        if (isAccumulativeMachine && (m?.actualCreditsIn == null || m?.actualCreditsOut == null)) {
          errors.push(
            `[Tragamoneda] localMachineId ${lmId}: con fórmula requiere CreditsIn y CreditsOut.`
          );
        }
        if (!isAccumulativeMachine && (m?.netCreditsIn == null || m?.netCreditsOut == null)) {
          errors.push(
            `[Tragamoneda] localMachineId ${lmId}: con fórmula con borrado requiere NetCreditsIn y NetCreditsOut.`
          );
        }
      }
    }
  }

  return errors;
};

export const buildCollectionPayload = ({
  machines = [],
  visitWithCollectionId,
  isEdit = false,
  prevRecords = [],
  userInputs = {},
  counterVerificationContextsByLocalMachineId = {},
}) => {
  const missing = [];
  const payload = machines
    .map((m) => {
      const base = toPayloadFromForm(m, visitWithCollectionId);
      if (!base) return null;
      const counterResult = buildCounterVerificationPayloadForMachine({
        machine: m,
        userInputs,
        counterVerificationContextsByLocalMachineId,
      });
      const baseWithCounterVerification = counterResult.payload
        ? { ...base, counterVerification: counterResult.payload }
        : base;
      if (!isEdit) return baseWithCounterVerification;
      const lastFromSelf = m.LastVisitMachineId;
      const lastFromPrev = prevRecords.find(
        (pr) => pr.LocalMachineId === m.LocalMachineId
      )?.LastVisitMachineId;
      const lastId = Number(lastFromSelf ?? lastFromPrev ?? 0);
      if (!lastId) missing.push(m);
      return { LastVisitMachineId: lastId || undefined, ...baseWithCounterVerification };
    })
    .filter(Boolean);

  return { payload, missing };
};

export const normalizeDraftMachineState = (machine) => {
  const raw = String(machine?.State ?? machine?.state ?? "").trim().toLowerCase();
  if (raw === "saved" || raw === "guardada") return "saved";
  if (raw === "not_collected" || raw === "not-collected" || raw === "no_recaudada") {
    return "not_collected";
  }
  if (raw === "pending" || raw === "pendiente") return "pending";

  if (machine?.VisitMachine) {
    return machine.VisitMachine.WasCollected === false ? "not_collected" : "saved";
  }
  if (machine?.WasCollected === false) return "not_collected";

  return "pending";
};

export const getDraftMachineStatusLabel = (machine) => {
  const state = normalizeDraftMachineState(machine);
  if (state === "saved") return "Guardada";
  if (state === "not_collected") return "No recaudada";
  return "Pendiente";
};

export const getDraftMachineDefaultWasCollected = (machine, explicitValue = undefined) => {
  if (typeof explicitValue === "boolean") return explicitValue;
  if (machine?.VisitMachine) return machine.VisitMachine.WasCollected !== false;
  if (typeof machine?.WasCollected === "boolean") return machine.WasCollected;
  return true;
};

export const resolveCreationDraftWasCollected = ({
  machine,
  keyId,
  userInputs = {},
  wasCollectedByLM = {},
} = {}) => {
  const resolvedKey = Number(keyId ?? machine?.LocalMachineId ?? machine?.Id ?? 0);
  if (Number.isFinite(resolvedKey) && resolvedKey > 0) {
    if (typeof userInputs?.[resolvedKey]?.WasCollected === "boolean") {
      return userInputs[resolvedKey].WasCollected;
    }
    if (typeof wasCollectedByLM?.[resolvedKey] === "boolean") {
      return wasCollectedByLM[resolvedKey];
    }
  }
  if (machine?.VisitMachine) return machine.VisitMachine.WasCollected !== false;
  return true;
};

export const normalizeCreationDraftEditorMachine = (machine = {}) => {
  const localMachineId = Number(
    machine?.LocalMachineId ??
    machine?.LocalMachine?.Id ??
    machine?.Id ??
    0
  );
  if (!Number.isFinite(localMachineId) || localMachineId <= 0) return machine;

  return {
    ...machine,
    Id: localMachineId,
    LocalMachineId: localMachineId,
    Machine: machine?.Machine
      ? { ...machine.Machine }
      : (
          machine?.MachineId != null ||
          machine?.MachineName != null ||
          machine?.SerialNumber != null ||
          machine?.LocalDisplayNumber != null
        )
        ? {
            Id: machine?.MachineId ?? machine?.Machine?.Id ?? null,
            Name: machine?.MachineName ?? machine?.Machine?.Name ?? null,
            SerialNumber: machine?.SerialNumber ?? machine?.Machine?.SerialNumber ?? null,
            LocalDisplayNumber:
              machine?.LocalDisplayNumber ?? machine?.Machine?.LocalDisplayNumber ?? null,
          }
        : machine?.Machine,
  };
};

export const resolveVisitMachineId = (row = {}) => {
  const parsed = Number(row?.VisitMachineId ?? row?.LastVisitMachineId ?? row?.Id ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const normalizeCompletedEditMachine = (row = {}) => {
  const visitMachineId = resolveVisitMachineId(row);
  const localMachineId = Number(row?.LocalMachineId ?? row?.LocalMachine?.Id ?? 0);
  const normalizedLocalMachineId =
    Number.isFinite(localMachineId) && localMachineId > 0 ? localMachineId : row?.LocalMachineId;

  return {
    ...row,
    VisitMachineId: visitMachineId,
    LastVisitMachineId: visitMachineId,
    Id: normalizedLocalMachineId,
    LocalMachineId: normalizedLocalMachineId,
  };
};

const COMPLETED_EDIT_UI_FIELDS = [
  "actualCreditsIn",
  "actualCreditsOut",
  "netCreditsIn",
  "netCreditsOut",
  "moneyIn",
  "moneyOut",
  "netMoneyIn",
  "netMoneyOut",
  "moneyCollected",
  "clientAmount",
  "ownerAmount",
];

const COMPLETED_EDIT_PRESERVED_FIELDS = [
  "CounterVerification",
  "HistoricalProfitPercentage",
  "HistoricalFormula",
  "HistoricalRent",
  "HistoricalLocalMachine",
  "ClientPercentage",
  "OwnerPercentage",
  "LocalMachineProfitPercentageId",
  "FormulaId",
  "Formula",
  "FormulaType",
  "FormulaValue",
  "RentId",
  "Rent",
  "RentPrice",
  "RentPaymentFrequency",
  "MachineTypeId",
  "MachineType",
  "WasCollected",
  "IsAccumulative",
  "HasReset",
  "PostResetStartIn",
  "PostResetStartOut",
  "PrevCreditsIn",
  "PrevCreditsOut",
  "PrevMoneyIn",
  "PrevMoneyOut",
  "CreditsIn",
  "CreditsOut",
  "NetCreditsIn",
  "NetCreditsOut",
  "MoneyIn",
  "MoneyOut",
  "NetMoneyIn",
  "NetMoneyOut",
  "CreditsCollected",
  "MoneyCollected",
  "ClientAmount",
  "OwnerAmount",
  "Note",
  "note",
];

const hasCompletedEditUiShape = (row = {}) =>
  COMPLETED_EDIT_UI_FIELDS.some((field) =>
    Object.prototype.hasOwnProperty.call(row || {}, field)
  );

const pickDefinedHistoricalFields = (row = {}) =>
  COMPLETED_EDIT_PRESERVED_FIELDS.reduce((acc, field) => {
    if (Object.prototype.hasOwnProperty.call(row || {}, field)) {
      acc[field] = row[field];
    }
    return acc;
  }, {});

export const normalizeCompletedEditDetailMachine = (row = {}) => {
  const hasUiShape = hasCompletedEditUiShape(row);
  const formMachine = hasUiShape ? {} : toFormMachine(row);
  const visitMachineId = row?.VisitMachineId ?? row?.LastVisitMachineId ?? row?.Id ?? null;
  const localMachineId = row?.LocalMachineId ?? row?.LocalMachine?.Id ?? formMachine?.LocalMachineId;
  const merged = {
    ...row,
    ...formMachine,
    ...pickDefinedHistoricalFields(row),
    VisitMachineId: visitMachineId,
    LastVisitMachineId: visitMachineId,
    LocalMachineId: localMachineId,
  };

  return normalizeCompletedEditMachine(merged);
};

export const replaceEditedVisitMachine = (rows = [], updatedRow = {}) => {
  const updated = normalizeCompletedEditDetailMachine(updatedRow);
  const updatedVisitMachineId = resolveVisitMachineId(updated);
  if (!updatedVisitMachineId) return rows;
  return (rows || []).map((row) =>
    resolveVisitMachineId(row) === updatedVisitMachineId
      ? normalizeCompletedEditDetailMachine({ ...row, ...updated })
      : row
  );
};

export const getCompletedEditMachineStatusLabel = (machine = {}) =>
  machine?.WasCollected === false ? "No recaudada" : "Guardada";

export const getCompletedEditDateState = ({
  persistedDate,
  candidateDate,
  bounds = null,
} = {}) => {
  const validFormat = typeof candidateDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(candidateDate);
  const changed = validFormat && candidateDate !== persistedDate;
  const beforeMin =
    validFormat && bounds?.MinSafeEditDate && candidateDate < bounds.MinSafeEditDate;
  const afterMax =
    validFormat && bounds?.MaxSafeEditDate && candidateDate > bounds.MaxSafeEditDate;
  return {
    validFormat,
    changed,
    withinRange: validFormat && !beforeMin && !afterMax,
    canSave: changed && !beforeMin && !afterMax,
  };
};

export const getCompletedEditMachineContextDateInput = ({
  isEditMode = false,
  persistedDate,
  candidateDate,
} = {}) => (isEditMode ? persistedDate : candidateDate);

export const canOpenCompletedEditMachine = ({ editDateState = {}, saving = false } = {}) =>
  !saving && editDateState?.changed !== true;

export const getCompletedEditExitState = ({
  isEditMode = false,
  isCollectionDirty = false,
  editDateChanged = false,
} = {}) => {
  if (!isEditMode) {
    return {
      isCompletedEditExit: false,
      canExitDirectly: false,
      requiresDialog: false,
    };
  }

  const hasPendingChanges = isCollectionDirty === true || editDateChanged === true;
  return {
    isCompletedEditExit: true,
    canExitDirectly: !hasPendingChanges,
    requiresDialog: hasPendingChanges,
  };
};

export const shouldInitializeCompletedEditBaseline = ({
  isEditMode = false,
  effectiveVisitId = null,
  loadedVisitId = null,
  initializedVisitId = null,
  sourceRows = [],
} = {}) => {
  const visitId = Number(effectiveVisitId);
  const loadedId = Number(loadedVisitId);
  if (!isEditMode) return false;
  if (!Number.isFinite(visitId) || visitId <= 0) return false;
  if (!Number.isFinite(loadedId) || loadedId !== visitId) return false;
  if (!Array.isArray(sourceRows) || !sourceRows.length) return false;
  return Number(initializedVisitId) !== visitId;
};

export const isCompletedEditDetailResponseForVisit = (detail = null, requestedVisitId = null) => {
  const requestedId = Number(requestedVisitId);
  const responseId = Number(detail?.visit?.Id);
  return (
    Number.isFinite(requestedId) &&
    requestedId > 0 &&
    Number.isFinite(responseId) &&
    responseId === requestedId
  );
};

export const getCompletedEditPersistedBaseline = ({
  editMachineRows = [],
  editSourceMachines = [],
} = {}) =>
  Array.isArray(editMachineRows) && editMachineRows.length
    ? editMachineRows
    : (Array.isArray(editSourceMachines) ? editSourceMachines : []);

export const buildCompletedEditMachineListFromPersistedBaseline = ({
  isEditMode = false,
  completedEditPersistedBaseline = [],
} = {}) => {
  if (!isEditMode) return [];
  return sortLocalMachinesFixed(
    (completedEditPersistedBaseline || []).map(normalizeCompletedEditMachine),
    { preferLocalDisplayNumber: true }
  );
};

export const hasIncompleteIndividualSlotEntryExit = (
  machine = {},
  userInputs = {},
  counterVerificationContextsByLocalMachineId = {}
) => {
  const type = getTypeName(machine);
  if (type !== "Tragamoneda" || hasRent(machine) || machine?.WasCollected === false) return false;

  const hasFormula = hasMachineOperatorFormula(machine);
  const isAccumulative = Boolean(machine?.IsAccumulative);
  const fields = hasFormula
    ? isAccumulative
      ? ["actualCreditsIn", "actualCreditsOut"]
      : ["netCreditsIn", "netCreditsOut"]
    : isAccumulative
      ? ["moneyIn", "moneyOut"]
      : ["netMoneyIn", "netMoneyOut"];

  if (fields.some((field) => machine?.[field] == null)) return true;

  if (!shouldSendCounterVerificationForMachine(machine, counterVerificationContextsByLocalMachineId)) {
    return false;
  }

  const lmId = Number(machine?.LocalMachineId ?? machine?.Id ?? 0);
  const inputs = userInputs?.[lmId] || {};
  const mode = inputs.CounterMode || "verify";
  if (mode !== "verify") return false;

  return (
    isBlankUserInputValue(inputs.CounterCurrentIn) ||
    isBlankUserInputValue(inputs.CounterCurrentOut)
  );
};

export const buildCompletedEditWasCollectedMap = (baseline = []) =>
  (baseline || []).reduce((acc, machine) => {
    const key = Number(machine?.LocalMachineId ?? machine?.Id);
    if (Number.isFinite(key) && key > 0) {
      acc[key] = isVisitMachineCollected(machine);
    }
    return acc;
  }, {});

export const getHistoricalProfitPercentageForEditMachine = (machine = {}) => {
  const id = machine?.HistoricalProfitPercentage?.Id ?? machine?.LocalMachineProfitPercentageId;
  const machineTypeId = machine?.MachineTypeId ?? machine?.MachineType?.Id;
  const clientPercentage =
    machine?.HistoricalProfitPercentage?.ClientPercentage ?? machine?.ClientPercentage;
  const ownerPercentage =
    machine?.HistoricalProfitPercentage?.OwnerPercentage ?? machine?.OwnerPercentage;

  if (
    id == null &&
    machineTypeId == null &&
    clientPercentage == null &&
    ownerPercentage == null
  ) {
    return null;
  }

  return {
    Id: id ?? null,
    MachineTypeId: machineTypeId ?? null,
    ClientPercentage: clientPercentage ?? null,
    OwnerPercentage: ownerPercentage ?? null,
  };
};

export const hasCompleteHistoricalProfitPercentage = (percentage = null) =>
  percentage != null &&
  percentage.Id != null &&
  Number.isFinite(Number(percentage.ClientPercentage)) &&
  Number.isFinite(Number(percentage.OwnerPercentage));

export const getEffectiveProfitPercentagesForMachine = ({
  machine,
  isEditMode = false,
  activeProfitPercentages = [],
} = {}) => {
  if (!isEditMode) return activeProfitPercentages || [];
  if (hasRent(machine)) return activeProfitPercentages || [];

  const historicalPercentage = getHistoricalProfitPercentageForEditMachine(machine);
  if (hasCompleteHistoricalProfitPercentage(historicalPercentage)) {
    return [historicalPercentage];
  }

  return [];
};

const readPersistedTotals = (source = null) => {
  if (!source) return null;
  const hasAmount = Object.prototype.hasOwnProperty.call(source, "TotalAmount");
  const hasClient = Object.prototype.hasOwnProperty.call(source, "TotalClientAmount");
  const hasOwner = Object.prototype.hasOwnProperty.call(source, "TotalOwnerAmount");
  if (!hasAmount || !hasClient || !hasOwner) return null;
  return {
    TotalAmount: Number(source.TotalAmount ?? 0),
    TotalClientAmount: Number(source.TotalClientAmount ?? 0),
    TotalOwnerAmount: Number(source.TotalOwnerAmount ?? 0),
  };
};

export const getCompletedEditPersistedTotals = ({
  state = null,
  bootDetail = null,
  visitWithCollection = null,
} = {}) => {
  const candidates = [
    state?.visitWithCollection,
    state?.VisitWithCollection,
    state,
    visitWithCollection,
    bootDetail?.visitWithCollection,
    bootDetail?.VisitWithCollection,
    bootDetail?.visit,
    bootDetail,
  ];

  for (const candidate of candidates) {
    const totals = readPersistedTotals(candidate);
    if (totals) return totals;
  }

  return null;
};

export const getDraftProgressCounts = (progress = {}) => {
  const expectedCount = Number(progress?.expectedCount ?? progress?.expectedMachines?.length ?? 0);
  const completedCount = Number(progress?.completedCount ?? 0);
  const pendingCount = Number(
    progress?.pendingCount ?? Math.max(expectedCount - completedCount, 0)
  );
  return {
    expectedCount: Number.isFinite(expectedCount) ? expectedCount : 0,
    completedCount: Number.isFinite(completedCount) ? completedCount : 0,
    pendingCount: Number.isFinite(pendingCount) ? pendingCount : 0,
  };
};

export const canCompleteDraftProgress = (progress = {}) => {
  if (typeof progress?.canComplete === "boolean") return progress.canComplete;
  const { expectedCount, completedCount, pendingCount } = getDraftProgressCounts(progress);
  return expectedCount > 0 && completedCount === expectedCount && pendingCount === 0;
};

export const getDraftPartialTotals = (progress = {}) => ({
  TotalAmount: Number(progress?.partialTotals?.TotalAmount ?? 0),
  TotalClientAmount: Number(progress?.partialTotals?.TotalClientAmount ?? 0),
  TotalOwnerAmount: Number(progress?.partialTotals?.TotalOwnerAmount ?? 0),
});

// The general list uses the same persisted VisitMachine amounts as the visit totals.
// The caller passes only the machines belonging to one visible type block.
export const getCollectionGroupSubtotal = (machines = [], { draft = false } = {}) => {
  const totals = { TotalAmount: 0, TotalClientAmount: 0, TotalOwnerAmount: 0 };
  const percentages = [];
  for (const machine of machines) {
    const persisted = draft ? machine?.VisitMachine : machine;
    const isSaved = !draft || normalizeDraftMachineState(machine) === "saved";
    if (isSaved && persisted && persisted.WasCollected !== false) {
      totals.TotalAmount += Number(persisted.MoneyCollected ?? persisted.moneyCollected ?? 0);
      totals.TotalClientAmount += Number(persisted.ClientAmount ?? persisted.clientAmount ?? 0);
      totals.TotalOwnerAmount += Number(persisted.OwnerAmount ?? persisted.ownerAmount ?? 0);
    }
    if (hasPresentationRent(machine)) {
      percentages.push(null);
    } else if (draft) {
      const historical = isSaved && persisted
        ? getHistoricalProfitPercentageForEditMachine(persisted)
        : null;
      const persistedId = persisted?.LocalMachineProfitPercentageId;
      const expectedId = machine?.ExpectedLocalMachineProfitPercentageId;
      const sameConfiguration = persistedId == null ||
        (expectedId != null && Number(persistedId) === Number(expectedId));
      percentages.push(
        historical?.ClientPercentage != null && historical?.OwnerPercentage != null
          ? historical
          : sameConfiguration
            ? {
                ClientPercentage: machine?.ExpectedClientPercentage,
                OwnerPercentage: machine?.ExpectedOwnerPercentage,
              }
            : null
      );
    } else if (persisted?.WasCollected !== false) {
      percentages.push(getHistoricalProfitPercentageForEditMachine(persisted));
    }
  }

  const applicable = percentages.filter((percentage) =>
    percentage?.ClientPercentage != null && percentage?.OwnerPercentage != null &&
    Number.isFinite(Number(percentage.ClientPercentage)) &&
    Number.isFinite(Number(percentage.OwnerPercentage))
  );
  let percentageStatus = "none";
  if (applicable.length && applicable.length === percentages.length) {
    const first = applicable[0];
    percentageStatus = applicable.every((percentage) =>
      Number(percentage.ClientPercentage) === Number(first.ClientPercentage) &&
      Number(percentage.OwnerPercentage) === Number(first.OwnerPercentage)
    ) ? "uniform" : "variable";
  }
  return {
    ...totals,
    percentageStatus,
    ClientPercentage: percentageStatus === "uniform" ? Number(applicable[0].ClientPercentage) : null,
    OwnerPercentage: percentageStatus === "uniform" ? Number(applicable[0].OwnerPercentage) : null,
  };
};

export const getNextPendingLocalMachineId = (machines = [], currentLocalMachineId = null) => {
  const ordered = sortLocalMachinesFixed(machines, { preferLocalDisplayNumber: true });
  const pending = ordered.filter((machine) => normalizeDraftMachineState(machine) === "pending");
  if (!pending.length) return null;

  const current = Number(currentLocalMachineId);
  if (Number.isFinite(current) && current > 0) {
    const currentIndex = ordered.findIndex(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === current
    );
    const afterCurrent = ordered
      .slice(currentIndex >= 0 ? currentIndex + 1 : 0)
      .find((machine) => normalizeDraftMachineState(machine) === "pending");
    if (afterCurrent) return Number(afterCurrent?.LocalMachineId ?? afterCurrent?.Id);
  }

  return Number(pending[0]?.LocalMachineId ?? pending[0]?.Id);
};

const mergeDraftMachineInfo = (fallback = {}, machine = {}) => {
  const hasAnyMachineInfo =
    fallback?.Machine ||
    machine?.Machine ||
    fallback?.MachineId != null ||
    machine?.MachineId != null ||
    fallback?.MachineName != null ||
    machine?.MachineName != null ||
    fallback?.SerialNumber != null ||
    machine?.SerialNumber != null ||
    fallback?.LocalDisplayNumber != null ||
    machine?.LocalDisplayNumber != null;

  if (!hasAnyMachineInfo) return undefined;

  const merged = {
    ...(fallback?.Machine || {}),
    ...(machine?.Machine || {}),
  };

  const id = machine?.Machine?.Id ?? machine?.MachineId ?? fallback?.Machine?.Id ?? fallback?.MachineId ?? null;
  const name =
    machine?.Machine?.Name ??
    machine?.MachineName ??
    fallback?.Machine?.Name ??
    fallback?.MachineName ??
    null;
  const serial = resolveMachineSerial(machine, fallback);
  const localDisplayNumber =
    machine?.Machine?.LocalDisplayNumber ??
    machine?.LocalDisplayNumber ??
    fallback?.Machine?.LocalDisplayNumber ??
    fallback?.LocalDisplayNumber ??
    null;

  return {
    ...merged,
    Id: id,
    Name: name,
    SerialNumber: serial,
    LocalDisplayNumber: localDisplayNumber,
  };
};

export const buildDraftMachineList = (progress = {}, fallbackMachines = []) => {
  const progressMachines = Array.isArray(progress?.expectedMachines)
    ? progress.expectedMachines
    : [];
  const persistedByLocalMachineId = new Map(
    (Array.isArray(progress?.visitMachines) ? progress.visitMachines : [])
      .map((machine) => [Number(machine?.LocalMachineId), machine])
  );
  const fallbackByLocalMachineId = new Map(
    (Array.isArray(fallbackMachines) ? fallbackMachines : [])
      .map((machine) => [Number(machine?.LocalMachineId ?? machine?.Id), machine])
  );

  const source = progressMachines.length ? progressMachines : fallbackMachines;
  return sortLocalMachinesFixed(
    (source || []).map((machine) => {
      const localMachineId = Number(machine?.LocalMachineId ?? machine?.Id);
      const persisted =
        machine?.VisitMachine ||
        persistedByLocalMachineId.get(localMachineId) ||
        null;
      const fallback = fallbackByLocalMachineId.get(localMachineId) || {};
      const mergedMachineInfo = mergeDraftMachineInfo(fallback, machine);
      const serialNumber = resolveMachineSerial(
        { ...machine, Machine: mergedMachineInfo },
        fallback
      );
      const mergedMachine = {
        ...fallback,
        ...machine,
        ...(mergedMachineInfo ? { Machine: mergedMachineInfo } : {}),
        SerialNumber: serialNumber,
        ...(persisted ? { VisitMachine: persisted } : {}),
        LocalMachineId: localMachineId,
      };
      return {
        ...mergedMachine,
        State: normalizeDraftMachineState(mergedMachine),
      };
    }),
    { preferLocalDisplayNumber: true }
  );
};

export const getLocalMachinePresentationDetails = (machines = [], localId = null) => {
  const parsedLocalId = Number(localId);
  if (!Number.isFinite(parsedLocalId) || parsedLocalId <= 0) return [];

  return (Array.isArray(machines) ? machines : []).filter((machine) => {
    const machineLocalId = Number(machine?.LocalId);
    return !Number.isFinite(machineLocalId) || machineLocalId === parsedLocalId;
  });
};

const normalizeDraftHydrationMachineType = (machine = {}, persisted = null) => {
  const source = machine?.MachineType ?? persisted?.MachineType ?? null;
  const type =
    source && typeof source === "object"
      ? source.Type ?? source.type ?? null
      : source ?? machine?.MachineTypeType ?? persisted?.MachineTypeType ?? null;
  const id =
    (source && typeof source === "object" ? source.Id ?? source.id : null) ??
    machine?.MachineTypeId ??
    persisted?.MachineTypeId ??
    null;

  if (type == null && id == null) return source;
  return {
    ...(source && typeof source === "object" ? source : {}),
    Id: id,
    Type: type,
  };
};

const normalizeDraftHydrationFormula = (machine = {}, persisted = null) => {
  if (machine?.Formula !== undefined) return machine.Formula;
  if (persisted?.Formula !== undefined) return persisted.Formula;

  const formulaId = machine?.FormulaId ?? persisted?.FormulaId ?? null;
  const formulaType = machine?.FormulaType ?? persisted?.FormulaType ?? null;
  const formulaValue = machine?.FormulaValue ?? persisted?.FormulaValue ?? null;
  const formulaName = machine?.FormulaName ?? persisted?.FormulaName ?? null;

  if (formulaId == null && formulaType == null && formulaValue == null && formulaName == null) {
    return null;
  }

  return {
    Id: formulaId,
    Type: formulaType,
    Value: formulaValue,
    Name: formulaName,
  };
};

export const buildDraftInputHydrationMachines = (progress = {}) => {
  const expectedMachines = Array.isArray(progress?.expectedMachines)
    ? progress.expectedMachines
    : [];
  const persistedByLocalMachineId = new Map(
    (Array.isArray(progress?.visitMachines) ? progress.visitMachines : [])
      .map((machine) => [Number(machine?.LocalMachineId), machine])
  );

  return expectedMachines.reduce((acc, expectedMachine) => {
    const localMachineId = Number(expectedMachine?.LocalMachineId ?? expectedMachine?.Id);
    if (!Number.isFinite(localMachineId) || localMachineId <= 0) return acc;

    const persisted =
      expectedMachine?.VisitMachine ??
      persistedByLocalMachineId.get(localMachineId) ??
      null;
    if (!persisted) return acc;

    const machineType = normalizeDraftHydrationMachineType(expectedMachine, persisted);
    const formula = normalizeDraftHydrationFormula(expectedMachine, persisted);

    acc.push({
      ...expectedMachine,
      ...persisted,
      LocalMachineId: localMachineId,
      VisitMachineId:
        persisted?.VisitMachineId ??
        persisted?.LastVisitMachineId ??
        persisted?.Id ??
        null,
      LastVisitMachineId:
        persisted?.LastVisitMachineId ??
        persisted?.VisitMachineId ??
        persisted?.Id ??
        null,
      MachineType: machineType,
      MachineTypeId:
        expectedMachine?.MachineTypeId ??
        persisted?.MachineTypeId ??
        (machineType && typeof machineType === "object" ? machineType.Id : null),
      Formula: formula,
      FormulaId: expectedMachine?.FormulaId ?? persisted?.FormulaId ?? null,
      FormulaType: expectedMachine?.FormulaType ?? persisted?.FormulaType ?? null,
      FormulaValue: expectedMachine?.FormulaValue ?? persisted?.FormulaValue ?? null,
      Rent: expectedMachine?.Rent ?? persisted?.Rent ?? null,
      RentId:
        expectedMachine?.RentId ??
        expectedMachine?.LocalMachineRentId ??
        persisted?.RentId ??
        null,
      MachineId:
        expectedMachine?.MachineId ??
        expectedMachine?.Machine?.Id ??
        persisted?.MachineId ??
        null,
      MachineName:
        expectedMachine?.MachineName ??
        expectedMachine?.Machine?.Name ??
        persisted?.MachineName ??
        null,
      LocalDisplayNumber:
        expectedMachine?.LocalDisplayNumber ??
        expectedMachine?.Machine?.LocalDisplayNumber ??
        persisted?.LocalDisplayNumber ??
        null,
      VisitMachine: persisted,
    });

    return acc;
  }, []);
};

const hasDirtyWasCollected = (dirtyWasCollectedByLM = {}, localMachineId) => {
  if (!dirtyWasCollectedByLM) return false;
  if (dirtyWasCollectedByLM instanceof Set) return dirtyWasCollectedByLM.has(Number(localMachineId));
  return dirtyWasCollectedByLM[localMachineId] === true || dirtyWasCollectedByLM[Number(localMachineId)] === true;
};

export const mergeDraftHydratedInputs = ({
  prevInputs = {},
  hydratedInputs = {},
  dirtyWasCollectedByLM = {},
} = {}) => {
  const mergedInputs = { ...(prevInputs || {}) };

  Object.entries(hydratedInputs || {}).forEach(([localMachineId, persistedInputs]) => {
    const localInputs = prevInputs?.[localMachineId] || {};
    const mergedMachineInputs = {
      ...(persistedInputs || {}),
      ...localInputs,
    };

    if (
      !hasDirtyWasCollected(dirtyWasCollectedByLM, localMachineId) &&
      Object.prototype.hasOwnProperty.call(persistedInputs || {}, "WasCollected")
    ) {
      mergedMachineInputs.WasCollected = persistedInputs.WasCollected;
    }

    mergedInputs[localMachineId] = mergedMachineInputs;
  });

  return mergedInputs;
};

export const mergeDraftHydratedWasCollected = ({
  prevWasCollectedByLM = {},
  hydratedWasCollectedByLM = {},
  dirtyWasCollectedByLM = {},
} = {}) => {
  const mergedWasCollected = { ...(prevWasCollectedByLM || {}) };

  Object.entries(hydratedWasCollectedByLM || {}).forEach(([localMachineId, persistedWasCollected]) => {
    if (
      hasDirtyWasCollected(dirtyWasCollectedByLM, localMachineId) &&
      typeof prevWasCollectedByLM?.[localMachineId] === "boolean"
    ) {
      mergedWasCollected[localMachineId] = prevWasCollectedByLM[localMachineId];
      return;
    }

    mergedWasCollected[localMachineId] = persistedWasCollected;
  });

  return mergedWasCollected;
};
